import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { ContextMenu } from 'radix-ui';
import {
  ChevronsDownUp,
  EyeOff,
  FileText,
  Plus,
  Search,
  Settings,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import type { PageNode } from '@nexus/store';
import { useStore } from '../db/context';
import { useUi } from '../state/ui';
import { formatKeys, SHORTCUT_KEYS } from '../shortcuts';
import { PageTree } from './PageTree';
import { PageMenu } from './PageMenu';
import { useFavorites } from '../lib/favorites';
import { useTemplatesFolderId } from '../templates/folder';
import { useMemosFolderId } from '../memos/data';
import { usePlugins, usePluginsFolderId } from '../plugins/runtime';
import { useCatalog } from '../state/catalog';
import { cn } from '../lib/cn';
import { ModuleIcon } from './ModuleIcon';
import { setHidden, useMenuEditor, useSidebarMenu, useUpdateSidebarMenu } from '../lib/sidebarMenu';
import { SyncBadge } from '../sync/AccountSettings';
import { useSyncState } from '../sync/useSync';
import { useTasks } from '../db/tasks';
import { isOpen } from '@nexus/core';
import { todayIso } from './tasks/format';
import { PageIcon } from './PageIcon';
import { WorkspacesSection } from '../workspaces/WorkspacesSection';
import { PendingInvites } from '../workspaces/PendingInvites';
import { useWorkspaces } from '../workspaces/state';

interface MenuItemDef {
  label: string;
  icon: ReactNode;
  active: boolean;
  onClick: () => void;
  extra?: ReactNode;
}

export function Sidebar({ tree, activePageId }: { tree: PageNode[]; activePageId: string | null }) {
  // Templates live in the Sjablonen overview, not in the page tree.
  const templatesFolderId = useTemplatesFolderId();
  const memosFolderId = useMemosFolderId();
  const pluginsFolderId = usePluginsFolderId();
  const plugins = usePlugins();
  const { t } = useTranslation();
  const store = useStore();
  const { open, set, aiOpen, collapseAll } = useUi();
  const tasks = useTasks();
  const today = todayIso();
  const session = useSyncState((s) => s.session);
  const accountName =
    (session?.user.user_metadata?.display_name as string | undefined) ||
    session?.user.email ||
    null;
  const due = tasks?.filter((x) => isOpen(x.status) && x.due && x.due <= today).length ?? 0;

  const newPage = async () => {
    const page = await store.createPage({ title: '' });
    open({ kind: 'page', pageId: page.id });
  };

  const current = useUi((s) => s.tabs[s.active]);
  const here = (kind: string) => current?.kind === kind;
  const item = (active = false) =>
    cn(
      'mx-2 flex h-8 w-[calc(100%-1rem)] items-center gap-2.5 rounded-lg px-1.5 text-sm transition-colors',
      active ? 'bg-bg font-medium text-fg shadow-sheet' : 'text-fg hover:bg-bg-hover',
    );

  /** The fixed menu's items; which show, and in what order, the user chooses. */
  const items: Record<string, MenuItemDef> = {
    dashboard: {
      label: t('dashboard.label'),
      icon: <ModuleIcon kind="dashboard" />,
      active: here('dashboard'),
      onClick: () => open({ kind: 'dashboard' }),
    },
    ai: {
      label: t('ai.title'),
      icon: <ModuleIcon kind="ai" />,
      active: aiOpen,
      onClick: () => set({ aiOpen: !aiOpen }),
      extra: (
        <kbd className="font-sans text-[11px] text-fg-muted">{formatKeys(SHORTCUT_KEYS.ai)}</kbd>
      ),
    },
    tasks: {
      label: t('tasks.title'),
      icon: <ModuleIcon kind="tasks" />,
      active: here('tasks'),
      onClick: () => open({ kind: 'tasks', tab: 'today' }),
      extra: due > 0 && (
        <span
          className="rounded-full bg-accent-soft px-1.5 text-xs font-medium text-accent-ink"
          aria-label={t('tasks.dueBadge', { count: due })}
        >
          {due}
        </span>
      ),
    },
    projects: {
      label: t('projects.title'),
      icon: <ModuleIcon kind="projects" />,
      active: here('projects'),
      onClick: () => open({ kind: 'projects' }),
    },
    memos: {
      label: t('memos.title'),
      icon: <ModuleIcon kind="memos" />,
      active: here('memos'),
      onClick: () => open({ kind: 'memos' }),
    },
    graph: {
      label: t('graph.title'),
      icon: <ModuleIcon kind="graph" />,
      active: here('graph'),
      onClick: () => open({ kind: 'graph' }),
    },
    templates: {
      label: t('templates.title'),
      icon: <ModuleIcon kind="templates" />,
      active: here('templates'),
      onClick: () => open({ kind: 'templates' }),
    },
    plugins: {
      label: t('plugins.title'),
      icon: <ModuleIcon kind="plugins" />,
      active: here('plugins'),
      onClick: () => open({ kind: 'plugins' }),
    },
    tips: {
      label: t('tips.title'),
      icon: <ModuleIcon kind="tips" />,
      active: here('tips'),
      onClick: () => open({ kind: 'tips' }),
    },
    // Views added by enabled plugins.
    ...Object.fromEntries(
      (plugins ?? [])
        .filter((p) => p.enabled)
        .flatMap((p) =>
          p.manifest.views.map((v) => [
            `plugin:${p.pageId}:${v.id}`,
            {
              label: v.title,
              icon: (
                <span aria-hidden className="mod-icon mod-plugins size-6 text-xs">
                  <PageIcon icon={v.icon} fallback={'🧩'} />
                </span>
              ),
              active:
                current?.kind === 'plugin' &&
                current.pluginId === p.pageId &&
                current.viewId === v.id,
              onClick: () => open({ kind: 'plugin', pluginId: p.pageId, viewId: v.id }),
            },
          ]),
        ),
    ),
  };
  const menu = useSidebarMenu(Object.keys(items));
  const updateMenu = useUpdateSidebarMenu(Object.keys(items));

  return (
    <nav aria-label={t('sidebar.label')} className="flex h-full w-64 shrink-0 flex-col bg-app">
      <div className="flex h-12 items-center gap-2 px-4 text-[15px] font-semibold tracking-tight">
        <img src="/icon.svg" alt="" className="size-6" />
        <span className="flex-1">Nexus Open</span>
        <button
          type="button"
          onClick={() => useMenuEditor.getState().setOpen(true)}
          title={t('sidebarMenu.customize')}
          aria-label={t('sidebarMenu.customize')}
          className="grid size-7 place-items-center rounded-full text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <SlidersHorizontal className="size-4" />
        </button>
      </div>
      <button
        type="button"
        className="mx-2 mb-1 flex h-9 w-[calc(100%-1rem)] items-center gap-2 rounded-xl border border-border bg-bg px-2.5 text-sm text-fg-muted hover:text-fg"
        onClick={() => set({ paletteOpen: true })}
      >
        <Search className="size-4" />
        <span className="min-w-0 flex-1 truncate text-left">{t('sidebar.search')}</span>
        <kbd className="rounded bg-bg-subtle px-1.5 font-sans text-[11px]">
          {formatKeys(SHORTCUT_KEYS.palette)}
        </kbd>
      </button>
      <button type="button" className={item()} onClick={() => void newPage()}>
        <span aria-hidden className="mod-icon mod-dashboard size-6">
          <Plus className="size-3.5" />
        </span>
        <span className="flex-1 text-left">{t('sidebar.newPage')}</span>
      </button>
      <div className="h-2" />
      {menu
        .filter((e) => !e.hidden && items[e.id])
        .map((e) => {
          const x = items[e.id]!;
          return (
            <ContextMenu.Root key={e.id}>
              <ContextMenu.Trigger asChild>
                <button
                  type="button"
                  className={item(x.active)}
                  aria-pressed={e.id === 'ai' ? aiOpen : undefined}
                  onClick={x.onClick}
                >
                  {x.icon}
                  <span className="min-w-0 flex-1 truncate text-left">{x.label}</span>
                  {x.extra}
                </button>
              </ContextMenu.Trigger>
              <ContextMenu.Portal>
                <ContextMenu.Content className="nx-pop-in z-[70] min-w-48 rounded-xl border border-border bg-bg p-1 text-sm shadow-pop">
                  <ContextMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 outline-none data-[highlighted]:bg-bg-hover"
                    onSelect={() => void updateMenu((m) => setHidden(m, e.id, true))}
                  >
                    <EyeOff className="size-4" />
                    {t('sidebarMenu.hide', { label: x.label })}
                  </ContextMenu.Item>
                  <ContextMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 outline-none data-[highlighted]:bg-bg-hover"
                    onSelect={() => useMenuEditor.getState().setOpen(true)}
                  >
                    <SlidersHorizontal className="size-4" />
                    {t('sidebarMenu.customize')}
                  </ContextMenu.Item>
                </ContextMenu.Content>
              </ContextMenu.Portal>
            </ContextMenu.Root>
          );
        })}

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        <Favorites activePageId={activePageId} />
        <div className="mt-3 flex items-center pr-2 pl-3">
          <h2 className="flex-1 pb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">
            {t('sidebar.pages')}
          </h2>
          <button
            type="button"
            onClick={collapseAll}
            title={t('sidebar.collapseAll')}
            aria-label={t('sidebar.collapseAll')}
            className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          >
            <ChevronsDownUp className="size-3.5" />
          </button>
        </div>
        <PageTree
          tree={tree.filter(
            (n) => n.id !== templatesFolderId && n.id !== pluginsFolderId && n.id !== memosFolderId,
          )}
          activePageId={activePageId}
        />
        <PendingInvites />
        <WorkspacesSection activePageId={activePageId} />
      </div>

      <div className="py-2">
        <button
          type="button"
          className={item(here('trash'))}
          onClick={() => open({ kind: 'trash' })}
        >
          <ModuleIcon kind="trash" icon={Trash2} />
          {t('sidebar.trash')}
        </button>
        <button
          type="button"
          className={item()}
          title={accountName ? t('sidebar.settings') : undefined}
          // Signed in, the label is the account; keep "Settings" in the name for screen readers.
          aria-label={accountName ? `${accountName} (${t('sidebar.settings')})` : undefined}
          onClick={() => set({ settingsOpen: true })}
        >
          <ModuleIcon kind="settings" icon={Settings} />
          <span className="min-w-0 flex-1 truncate text-left">
            {accountName ?? t('sidebar.settings')}
          </span>
          <SyncBadge compact />
        </button>
      </div>
    </nav>
  );
}

/** Starred pages, above the page tree (only when there are any). */
function Favorites({ activePageId }: { activePageId: string | null }) {
  const { t } = useTranslation();
  const favorites = useFavorites();
  const byId = useCatalog((s) => s.byId);
  const open = useUi((s) => s.open);
  const workspaces = useWorkspaces();
  const pages = favorites.map((id) => byId.get(id)).filter((p) => !!p);
  if (pages.length === 0) return null;
  return (
    <section aria-label={t('sidebar.favorites')} className="mt-3">
      <h2 className="px-3 pb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">
        {t('sidebar.favorites')}
      </h2>
      <ul>
        {pages.map((p) => (
          <li key={p.id}>
            <PageMenu
              pageId={p.id}
              title={p.title || t('page.untitled')}
              workspaceId={p.workspaceId}
              readOnly={
                !!p.workspaceId &&
                workspaces.find((w) => w.id === p.workspaceId)?.permission === 'read'
              }
            >
              <button
                type="button"
                onClick={(e) => open({ kind: 'page', pageId: p.id }, e.ctrlKey || e.metaKey)}
                className={cn(
                  'mx-2 flex h-8 w-[calc(100%-1rem)] items-center gap-2 rounded-lg px-1.5 text-sm',
                  p.id === activePageId ? 'bg-bg font-medium shadow-sheet' : 'hover:bg-bg-hover',
                )}
              >
                <span aria-hidden className="grid size-5 place-items-center text-fg-muted">
                  <PageIcon icon={p.icon} fallback={<FileText className="size-4" />} />
                </span>
                <span className="truncate">{p.title || t('page.untitled')}</span>
              </button>
            </PageMenu>
          </li>
        ))}
      </ul>
    </section>
  );
}
